import ROUTER from '@/shared/config/routes'

export interface ApiMenuItem {
  id: string
  label: string
  routerLink?: string | null
  icon?: string | null
  children?: ApiMenuItem[]
}

export interface MenuItemNode {
  key: string
  label: string
  path?: string
  icon?: string
  /** Mục dẫn ra trang ngoài SPA (vd. Razor của BE): bấm là tải trang, không đi qua router. */
  externalUrl?: string
  children?: MenuItemNode[]
}

const EXCLUDED_MENU_PATHS = new Set(['/danh-muc-cau-hinh', ROUTER.TAI_KHOAN_DAI_DIEN])
const EXCLUDED_MENU_LABELS = new Set([
  'Danh mục và cấu hình',
  'Tài khoản đại diện',
  'Quản lý người đại diện',
  'Người đại diện',
])

const isExcludedMenuItem = (item: {
  path?: string
  routerLink?: string | null
  label?: string
}): boolean => {
  if (item.path && EXCLUDED_MENU_PATHS.has(item.path)) return true
  if (item.routerLink && EXCLUDED_MENU_PATHS.has(item.routerLink)) return true
  if (item.label && EXCLUDED_MENU_LABELS.has(item.label.trim())) return true
  return false
}

export const mapApiMenuToNodes = (items: ApiMenuItem[]): MenuItemNode[] =>
  (items ?? [])
    .filter((item) => !isExcludedMenuItem(item))
    .map((item) => {
      const children = item.children?.length ? mapApiMenuToNodes(item.children) : undefined
      return {
        key: item.id,
        label: item.label,
        path: item.routerLink ?? undefined,
        icon: item.icon ?? undefined,
        ...(children ? { children } : {}),
      }
    })
    .filter((item) => item.path || (item.children && item.children.length > 0))

/**
 * Tự động gắn mục "Văn bản đã ký" ngay dưới "Văn bản chờ ký" trong nhóm Ký số
 * cho tài khoản có quyền ký nháy (tài khoản có mục "Văn bản chờ ký").
 */
const injectSignedDocumentsForSigners = (nodes: MenuItemNode[]): MenuItemNode[] => {
  return nodes.map((node) => {
    if (node.children && node.children.length > 0) {
      const hangChoKyIndex = node.children.findIndex(
        (c) => c.path === ROUTER.HANG_CHO_KY || c.key?.includes('hang-cho-ky')
      )
      const alreadyHasSigned = node.children.some(
        (c) =>
          c.path === ROUTER.VAN_BAN_DA_KY ||
          c.path === '/van-ban-da-ky' ||
          c.path === '/ho-so-da-ky' ||
          c.label === 'Văn bản đã ký'
      )

      if (hangChoKyIndex !== -1 && !alreadyHasSigned) {
        const signedNode: MenuItemNode = {
          key: 'van-ban-da-ky',
          label: 'Văn bản đã ký',
          path: ROUTER.VAN_BAN_DA_KY,
          icon: 'FileCheck',
        }
        const updatedChildren = [...node.children]
        updatedChildren.splice(hangChoKyIndex + 1, 0, signedNode)
        return {
          ...node,
          children: injectSignedDocumentsForSigners(updatedChildren),
        }
      }

      return {
        ...node,
        children: injectSignedDocumentsForSigners(node.children),
      }
    }
    return node
  })
}

/**
 * Xây dựng cây menu hiệu dụng:
 * - Khi `listMenu === null`: đang tải, trả về rỗng.
 * - Loại bỏ các trang không dùng (Danh mục và cấu hình /danh-muc-cau-hinh).
 * - Loại bỏ các nhóm cha rỗng sau khi lọc.
 * - Với tài khoản thường có quyền ký: gắn "Văn bản đã ký" dưới "Văn bản chờ ký".
 */
export const buildEffectiveMenu = (listMenu: MenuItemNode[] | null): MenuItemNode[] => {
  if (listMenu === null) return []

  const filterNodes = (nodes: MenuItemNode[]): MenuItemNode[] =>
    nodes
      .filter((n) => !isExcludedMenuItem(n))
      .map((n) => {
        if (!n.children) return n
        const children = filterNodes(n.children)
        return { ...n, children: children.length ? children : undefined }
      })
      .filter((n) => n.path || (n.children && n.children.length > 0))

  return injectSignedDocumentsForSigners(filterNodes(listMenu))
}

/**
 * Chuỗi tổ tiên từ gốc tới mục menu khớp `pathname`, dùng dựng breadcrumb.
 *
 * Antd Pro lấy breadcrumb từ **cây menu**, không phải từ URL: nhãn là nhãn menu, và
 * màn không nằm trong menu thì **không có breadcrumb** — nên hàm này trả rỗng chứ
 * không đoán nhãn từ segment.
 */
export const menuTrail = (nodes: MenuItemNode[], pathname: string): MenuItemNode[] => {
  for (const node of nodes) {
    if (node.path && node.path === pathname) return [node]
    if (node.children) {
      const trail = menuTrail(node.children, pathname)
      if (trail.length > 0) {
        const nodePath = node.path ?? firstRealPath(node.children)
        return [{ ...node, path: nodePath }, ...trail]
      }
    }
  }
  return []
}

/** Danh sách leaf phẳng — dùng để chọn trang mặc định sau đăng nhập. */
export const flattenLeaves = (nodes: MenuItemNode[]): MenuItemNode[] =>
  nodes.flatMap((n) =>
    n.children?.length ? flattenLeaves(n.children) : [{ ...n, path: n.path ?? n.key }]
  )

/** Leaf có `routerLink` thật (không lấy fallback `key` của flattenLeaves). */
const firstRealPath = (nodes: MenuItemNode[]): string | undefined => {
  for (const n of nodes) {
    const found = n.children?.length ? firstRealPath(n.children) : n.path
    if (found) return found
  }
  return undefined
}

/**
 * Trang đích sau đăng nhập:
 * - Superadmin → `TRANG_CHU_QUAN_TRI`, nhưng chỉ khi menu hiệu dụng có ít nhất một leaf
 *   (cùng tiêu chí `allowedLeafCount` của AdminRoutes). Menu rỗng → `ROUTER.HOME`, nếu không
 *   sẽ vào AdminRoutes, gặp 403 rồi quay lại vòng lặp.
 * - Mọi role khác: nếu menu có leaf `TRANG_CHU_QUAN_TRI` thì ưu tiên vào đó.
 * - Chưa có dashboard (BE chưa seed) → path của leaf đầu tiên có path hợp lệ.
 * - Không có leaf nào có path → `ROUTER.HOME` (không đoán, không 403).
 */
export const getLandingPath = (menu: MenuItemNode[], isSuperAdmin: boolean): string => {
  if (isSuperAdmin) {
    return flattenLeaves(buildEffectiveMenu(menu)).length > 0
      ? ROUTER.TRANG_CHU_QUAN_TRI
      : ROUTER.HOME
  }
  if (flattenLeaves(menu).some((n) => n.path === ROUTER.TRANG_CHU_QUAN_TRI)) {
    return ROUTER.TRANG_CHU_QUAN_TRI
  }
  return firstRealPath(menu) ?? ROUTER.HOME
}
